import { useEffect, useRef, useState } from "react";

import { Button, LabelWrapper } from "@theorgchartdev/storybook";
import { QGreenhouseTokenRFC } from "@coreapp/CoreApp/QGreenhouseTokenRFC";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appcstr } from "@coreapp/utils/appcstr";
import { GetWSMgr } from "@/Utils/StateLib";
import { DATA_SOURCES_TEST_PREFIX } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/Utils";

type AuthPayload = {
  state: string;
};

// The callback page broadcasts only the state; the tokens never leave the server until the
// opener trades that state on an authenticated call.
const isAuthPayload = (payload: any): payload is AuthPayload => {
  return typeof payload?.state === "string";
};

type Props = {
  value: Partial<{
    refreshToken: string;
  }>;
  onChange: (patch: Record<string, any>) => void;
};

export const GreenhouseOAuthSetup = (props: Props) => {
  const [hasOpenPopup, setPopupOpen] = useState(false);
  const [hasAuthError, setAuthError] = useState(false);
  const [isAuthenticating, setAuthenticating] = useState(false);

  // Constructed on first use, not in useRef's argument: that expression is evaluated on every
  // render, so a channel would be opened and discarded each time without ever being closed.
  const channelRef = useRef<BroadcastChannel | null>(null);
  const getChannel = () => {
    if (!channelRef.current) channelRef.current = new BroadcastChannel("oidc");
    return channelRef.current;
  };

  // One controller per authorization attempt: aborting it drops both the message listener and
  // the popup poll, so closing the popup and retrying cannot leave a second listener behind to
  // fire a duplicate token exchange whose failure would overwrite the first one's success.
  const attemptRef = useRef<AbortController | null>(null);
  const endAttempt = () => {
    attemptRef.current?.abort();
    attemptRef.current = null;
  };

  useEffect(
    () => () => {
      endAttempt();
      channelRef.current?.close();
      channelRef.current = null;
    },
    []
  );

  const handleBroadcastMessage = async (event: MessageEvent) => {
    try {
      setAuthenticating(true);
      setAuthError(false);
      const { data } = event;
      if (!isAuthPayload(data)) {
        throw new Error();
      }

      const wsMgr = GetWSMgr();
      const tokenRFC = new QGreenhouseTokenRFC();
      tokenRFC.InitRFC();
      tokenRFC.SetParams(wsMgr?.GetAccountName(), wsMgr?.GetUserName(), data.state);
      const responsePromise = tokenRFC.GetPromise();
      Globals._rfcMgr?.QueueRFC(tokenRFC);

      const response = await responsePromise;
      const result = JSON.parse(response);
      if (!result["success"]) {
        throw new Error();
      }

      // One patch rather than three onChange calls: each would rebuild from the same stale
      // config and the last write would drop the other two tokens.
      props.onChange({
        refreshToken: result.data.refreshToken,
        accessToken: result.data.accessToken,
        accessTokenExpiresAt: result.data.accessTokenExpiresAt
      });
    } catch {
      setAuthError(true);
    } finally {
      setAuthenticating(false);
    }
  };

  const handleAuthenticate = () => {
    const wsMgr = GetWSMgr();
    const orgName = wsMgr?.GetAccountName();
    const userName = wsMgr?.GetUserName();
    if (!orgName || !userName) {
      setAuthError(true);
      return;
    }

    endAttempt();
    setPopupOpen(true);
    setAuthError(false);

    const greenhouseURL = new URL(
      `${document.location.protocol}//${document.location.host}/ServerSide/Endpoints/qgreenhouseredirect`
    );
    greenhouseURL.searchParams.append("org", orgName);
    greenhouseURL.searchParams.append("user", userName);

    const popup = window.open(greenhouseURL, "popup", "popup=true");
    if (!popup) {
      // Bail before listening, so a later authorization cannot be picked up by a handler
      // this click left behind.
      setPopupOpen(false);
      setAuthError(true);
      return;
    }

    const attempt = new AbortController();
    attemptRef.current = attempt;

    // once: a second handler would collect a state the first already consumed, and that
    // failure would overwrite the success that just landed.
    getChannel().addEventListener("message", handleBroadcastMessage, {
      once: true,
      signal: attempt.signal
    });

    // Poll for the popup to be closed by user
    const intervalId = setInterval(() => {
      if (popup?.closed) {
        setPopupOpen(false);
        endAttempt();
      }
    }, 1000);
    attempt.signal.addEventListener("abort", () => clearInterval(intervalId));
  };

  const hasValidToken = typeof props.value.refreshToken === "string";

  return (
    <LabelWrapper
      message={hasAuthError ? appcstr("greenhouseautherr") : appcstr("greenhouseadminreq")}
      status={hasAuthError ? "error" : "default"}
      showMessage
    >
      <Button
        dataTestId={`${DATA_SOURCES_TEST_PREFIX}-greenhouse-oauth-authenticate-button`}
        isFullWidth
        modifier={"secondary"}
        loading={isAuthenticating || hasOpenPopup}
        onClick={handleAuthenticate}
        color={hasValidToken ? "success" : undefined}
        iconProps={
          hasValidToken
            ? {
                name: "check"
              }
            : undefined
        }
      >
        {appcstr(hasValidToken ? "greenhousereconnect" : "greenhouseconnect")}
      </Button>
    </LabelWrapper>
  );
};
